/**
 * Service Worker registration for the DOOH Player.
 *
 * Fielded kiosks are unattended, so the app must converge on a newly deployed
 * build on its own (#171): the SW version is build-stamped, and this module
 * polls for a new sw.js and reloads once the new worker takes control.
 */

const SW_PATH = '/sw.js';

/** How often to check the server for a new build (a changed sw.js). */
const UPDATE_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SW] Service Worker not supported');
    return null;
  }

  // Reload when a *new* Service Worker takes control so a deployed build reaches
  // the kiosk without manual intervention. A page that starts UNCONTROLLED
  // (fresh install, or the first load after a one-time cache wipe) gets one
  // `controllerchange` from the initial clients.claim — that's not an update,
  // so it's skipped. Every subsequent controllerchange (a genuinely new worker
  // taking over) triggers exactly one reload. A page that starts controlled
  // reloads on its first controllerchange. `reloading` prevents any loop.
  //
  // NOTE: capturing whether the page merely *had* a controller once and gating
  // on it forever is wrong — a post-wipe page loads uncontrolled, so it would
  // permanently suppress the reload and never pick up the next deploy (verified
  // on the bench Pi, 2026-07-14).
  const startedControlled = !!navigator.serviceWorker.controller;
  let controllerChanges = 0;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    controllerChanges += 1;
    if (!startedControlled && controllerChanges === 1) return; // initial claim
    if (reloading) return;
    reloading = true;
    console.log('[SW] New build activated — reloading to apply update');
    window.location.reload();
  });

  try {
    const registration = await navigator.serviceWorker.register(SW_PATH, {
      scope: '/',
    });

    console.log('[SW] Service Worker registered:', registration.scope);

    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        console.log('[SW] New Service Worker state:', newWorker.state);
      });
    });

    // Poll for a new build. A changed sw.js → install → (skipWaiting) activate →
    // (clients.claim) controllerchange → reload above. Non-fatal when offline.
    setInterval(() => {
      registration.update().catch(() => {
        /* offline / transient — retried on the next tick */
      });
    }, UPDATE_INTERVAL_MS);

    return registration;
  } catch (err) {
    console.error('[SW] Service Worker registration failed:', err);
    return null;
  }
}
